import Cookie from 'js-cookie';
import omit from 'lodash/omit';
import queryString from 'query-string';

export const apiUrl = process.env.REACT_APP_API_URL + '/v1';

export function getToken(authLoginInfo = null) {
  authLoginInfo = authLoginInfo || (Cookie.get('authLoginInfo') ? JSON.parse(Cookie.get('authLoginInfo')) : null);
  if (authLoginInfo && authLoginInfo.token) {
    return authLoginInfo.token;
  }
}

export function getAuthorizationHeader(authLoginInfo = null) {
  const token = getToken(authLoginInfo);
  if (token) {
    return `Bearer ${token}`;
  }
}

export async function fetchData({ path, method, contentType = 'application/json', body, authLoginInfo = null }) {
  const url = `${apiUrl}${path}`;

  let data = {
    method,
    headers: {
      Accept: 'application/json',
    },
  };
  if (contentType !== 'multipart/form-data') {
    data.headers['Content-Type'] = contentType;
  }
  if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
    data.body = contentType === 'application/json' ? JSON.stringify(body) : body;
  }

  const authorizationHeader = getAuthorizationHeader(authLoginInfo);
  if (authorizationHeader) {
    data.headers.Authorization = authorizationHeader;
  }

  const response = await fetch(url, data);
  if (response.status === 204) {
    return null;
  }
  const responseJson = await response.json();
  if (response.status >= 200 && response.status < 300) {
    return responseJson;
  }
  throw {
    ...responseJson,
    statusCode: response.status,
  };
}

function defaultReadEntities(endpoint) {
  return async function (action) {
    const payload = {
      ...action.payload,
    };
    if (payload.filters) {
      payload.filters = JSON.stringify(payload.filters);
    }

    const q = queryString.stringify(payload, { skipNull: true });
    const path = `/${endpoint}${q ? `?${q}` : ''}`;
    const method = 'GET';

    const response = await fetchData({ path, method });
    return response;
  };
}

function defaultCreateEntity(endpoint, extension = null) {
  return async function (action) {
    const path = `/${endpoint}${action.payload.id ? `/${action.payload.id}` : ''}${extension ? `/${extension}` : ''}`;
    const method = 'POST';
    const body = omit(action.payload, ['id']);

    const response = await fetchData({ path, method, body });
    return response;
  };
}

function defaultReadEntity(endpoint, extension = null) {
  return async function (action) {
    const q = queryString.stringify(omit(action.payload, ['id']), { skipNull: true });
    const path = `/${endpoint}/${action.payload.id}${extension ? `/${extension}` : ''}${q ? `?${q}` : ''}`;
    const method = 'GET';

    const response = await fetchData({ path, method });
    return response;
  };
}

function defaultUpdateEntity(endpoint, suffix = null) {
  return async function (action) {
    const path = `/${endpoint}/${action.payload.id}` + (suffix ? `/${suffix}` : '');
    const method = 'PUT';
    const body = action.payload;

    const response = await fetchData({ path, method, body });
    return response;
  };
}

function defaultDeleteEntity(endpoint) {
  return async function (action) {
    const path = `/${endpoint}/${action.payload.id}`;
    const method = 'DELETE';
    const body = action.payload;

    const response = await fetchData({ path, method, body });
    return response;
  };
}

/* Auth */
export async function authForgetPassword(action) {
  const path = '/public/auth/forgetPassword';
  const method = 'POST';
  const body = action.payload;

  const response = await fetchData({ path, method, body });
  return response;
}

export async function authLogin(action) {
  const path = '/public/auth/login';
  const method = 'POST';
  const body = action.payload;

  const response = await fetchData({ path, method, body });
  return response;
}

export async function authRegister(action) {
  const path = '/public/auth/register';
  const method = 'POST';
  const body = action.payload;

  const response = await fetchData({ path, method, body });
  return response;
}

export async function authResendVerificationToken(action) {
  const path = '/public/auth/resendVerificationToken';
  const method = 'POST';
  const body = action.payload;

  const response = await fetchData({ path, method, body });
  return response;
}

export async function authResetPassword(action) {
  const path = '/public/auth/resetPassword';
  const method = 'POST';
  const body = action.payload;

  const response = await fetchData({ path, method, body });
  return response;
}

export async function authVerify(action) {
  const path = '/public/auth/verify';
  const method = 'POST';
  const body = action.payload;

  const response = await fetchData({ path, method, body });
  return response;
}

export async function authVerifyForgetPasswordToken(action) {
  const path = '/public/auth/verifyForgetPassword';
  const method = 'POST';
  const body = action.payload;

  const response = await fetchData({ path, method, body });
  return response;
}

/* Options */
export const readOptionsFinancialYear = defaultReadEntities('giver/options/financialYear');
export const readOptionsFund = defaultReadEntities('giver/options/fund');
export const readOptionsGiving = defaultReadEntities('giver/options/giving');
export const readOptionsReceipt = defaultReadEntities('giver/options/receipt');
export const readOptionsPaymentMethod = defaultReadEntities('giver/options/paymentMethod');
export const readOptionsService = defaultReadEntities('giver/options/service');
export const readOptionsAuthRegister = defaultReadEntities('public/options/authRegister');
export const readOptionsPublicFinancialYear = defaultReadEntities('public/options/financialYear');
export const readOptionsPublicFund = defaultReadEntities('public/options/fund');
export const readOptionsPublicGiving = defaultReadEntities('public/options/giving');
export const readOptionsPublicPaymentMethod = defaultReadEntities('public/options/paymentMethod');
export const readOptionsPublicService = defaultReadEntities('public/options/service');

/* Public */
export const createPublicGiving = defaultCreateEntity('public/givings');
export const readPublicGiving = defaultReadEntity('public/givings');

/* Giver */
export const readCurrent = async function () {
  const path = '/giver/current';
  const method = 'GET';

  const response = await fetchData({ path, method });
  return response;
};

export const updateCurrent = async function (action) {
  const path = '/giver/current';
  const method = 'PUT';
  const body = action.payload;

  const response = await fetchData({ path, method, body });
  return response;
};

export const createGiving = async function (action) {
  const path = '/giver/givings';
  const method = 'POST';
  const body = omit(action.payload, ['id']);

  const response = await fetchData({ path, method, body });
  return response;
};
export const readGiving = defaultReadEntity('giver/givings');
export const readGivings = defaultReadEntities('giver/givings');
export const readGivingsStatistics = defaultReadEntities('giver/givings/statistics');
export const updateRecurringGiving = async function (action) {
  const path = `/giver/givings/${action.payload.id}/recurring`;
  const method = 'PUT';
  const body = action.payload;

  const response = await fetchData({ path, method, body });
  return response;
};

export const createProduct = defaultCreateEntity('giver/products');
export const readProduct = defaultReadEntity('giver/products');
export const readProducts = defaultReadEntities('giver/products');
export const updateProduct = defaultUpdateEntity('giver/products');
export const deleteProduct = defaultDeleteEntity('giver/products');

export const createReceipt = defaultCreateEntity('giver/receipts');
export const readReceipt = defaultReadEntity('giver/receipts');
export const readReceipts = defaultReadEntities('giver/receipts');
export const updateReceipt = defaultUpdateEntity('giver/receipts');
export const deleteReceipt = defaultDeleteEntity('giver/receipts');

export const createSubscription = defaultCreateEntity('giver/subscriptions');
export const readSubscription = defaultReadEntity('giver/subscriptions');
export const readSubscriptions = defaultReadEntities('giver/subscriptions');
export const updateSubscription = defaultUpdateEntity('giver/subscriptions');
export const deleteSubscription = defaultDeleteEntity('giver/subscriptions');
export const cancelSubscription = defaultUpdateEntity('giver/subscriptions', 'cancel');